import { useEffect } from "react";
import { sendPublicFeedback } from "src/api/system";
import { buildComment, limitText, uniqueKey } from "./error-functions";

export default function GlobalErrorListeners() {
  useEffect(() => {
    const reportError = async (payload: any) => {
      try {
        await sendPublicFeedback(payload);
      } catch (err) {
        console.error("Failed to report global error:", err);
      }
    };

    const onError = (event: ErrorEvent) => {
      console.error("Global error", event.error || event.message);

      reportError({
        feature_key: uniqueKey("frontend_window_error"),
        reasons: ["window_error"],
        userId: 999999999,
        usefulness: 3,
        comment: buildComment({
          message: limitText(
            event.message || event.error?.message || "Unknown error",
            300
          ),
          stack: limitText(event.error?.stack, 900),
          file: limitText(event.filename, 300),
          line: event.lineno || null,
          col: event.colno || null,
        }),
        context: {
          path: window.location.pathname + window.location.search,
          locale: navigator.language,
          ts: new Date().toISOString(),
          url: window.location.href,
          pathname: window.location.pathname,
          userAgent: navigator.userAgent,
          source: "window.error",
        },
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection", event.reason);

      reportError({
        feature_key: uniqueKey("frontend_unhandled_rejection"),
        reasons: ["unhandled_rejection"],
        userId: 999999999,
        usefulness: 3,
        comment: buildComment({
          message: limitText(
            event.reason?.message ||
              (typeof event.reason === "string"
                ? event.reason
                : "Unhandled rejection"),
            300
          ),
          stack: limitText(event.reason?.stack, 900),
          reason: limitText(
            typeof event.reason === "string"
              ? event.reason
              : (() => {
                  try {
                    return JSON.stringify(event.reason);
                  } catch {
                    return String(event.reason);
                  }
                })(),
            600
          ),
        }),
        context: {
          path: window.location.pathname + window.location.search,
          locale: navigator.language,
          ts: new Date().toISOString(),
          url: window.location.href,
          pathname: window.location.pathname,
          userAgent: navigator.userAgent,
          source: "window.unhandledrejection",
        },
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return null;
}
